<template>
  <div v-if="type === 'textarea'" class="c-rect-input">
    <label
      :for="inputId"
      class="c-rect-input__textarea-label--before-invisible-textarea"
      :data-required-suffix="this.requiredSuffix"
      >{{ label }}</label
    >
    <textarea
      class="c-rect-input__invisible-textarea"
      :name="name"
      :required="required ? true : false"
      ref="invisibleTextarea"
      :maxlength="maxlength"
      v-model.lazy="value"
    ></textarea>
    <vue-editor
      :id="inputId"
      v-model.lazy="value"
      :editorToolbar="customToolbar"
      class="s-text s-text--vue"
    ></vue-editor>
    <span
      v-if="maxlength"
      class="c-rect-input__note"
      :style="remainingLengthStyle"
      >{{ remainingLengthText }}</span
    >
  </div>
  <div v-else-if="type === 'checkbox'" class="c-checkbox">
    <input
      type="checkbox"
      :name="name"
      class="c-checkbox__input"
      :checked="checked"
      :id="inputId"
      @click="checkboxClicked"
      :disabled="disabled"
      ref="boolvalue"
      @input="input"
      v-bind="$attrs"
    />
    <label
      :for="inputId"
      class="c-checkbox__label"
      :data-required-suffix="this.requiredSuffix"
      >{{ label }}</label
    >
  </div>
  <div v-else :class="`c-rect-input ${cssClasses}`">
    <input
      :id="inputId"
      :class="{ 'has-value': this.hasValue }"
      class="c-rect-input__input"
      v-model="dataValue"
      :type="type"
      ref="textvalue"
      :disabled="disabled"
      :required="required"
      :name="name"
      @input="input"
      :maxlength="maxlength"
      v-bind="$attrs"
      :noDecimal="noDecimal"
      v-on:keydown="keyhandler"
      :textInputShouldBehaveAsNumber="textInputShouldBehaveAsNumber"
    />
    <label
      :for="inputId"
      class="c-rect-input__label"
      :data-required-suffix="this.requiredSuffix"
      >{{ label }}</label
    >
    <span v-if="maxlength" class="c-rect-input__note">{{
      remainingLengthText
    }}</span>
  </div>
</template>

<script>
import { VueEditor } from "vue2-editor";
export default {
  name: "VueInput",
  inheritAttrs: false,
  components: {
    VueEditor
  },
  props: {
    inputId: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: "text"
    },
    label: {
      type: String,
      default: ""
    },
    value: {
      type: String,
      default: ""
    },
    required: {
      type: Boolean,
      default: true
    },
    checked: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: Number,
      required: false
    },
    name: {
      type: String,
      required: false
    },
    cssClasses: {
      type: String,
      default: ""
    },
    noDecimal: {
      type: Boolean,
      default: false
    },
    textInputShouldBehaveAsNumber: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dataValue: this.value,
      exceedingMaxlength: false,
      customToolbar: [
        [
          {
            header: [2, 3, false]
          },
          "bold",
          "italic",
          "link",
          {
            list: "bullet"
          },
          {
            list: "ordered"
          },
          {
            color: []
          },
          {
            background: []
          }
        ]
      ],
      selected: false
    };
  },
  computed: {
    hasValue() {
      return this.dataValue !== "";
    },
    requiredSuffix() {
      return this.required ? "*" : "";
    },
    remainingLength() {
      return this.maxlength - this.value.length;
    },
    remainingLengthText() {
      return `${this.remainingLength} tecken kvar`;
    },
    remainingLengthStyle() {
      let style = "";
      if (this.maxlength - this.value.length < 0) {
        style = "color: red;";
      }
      return style;
    }
  },
  watch: {
    value() {
      if (this.value.length > this.maxlength) {
        if (!this.exceedingMaxlength) {
          this.exceedingMaxlength = true;
          this.notifyExceedingMaxlength(true);
        }
      } else {
        this.dataValue = this.value;
        if (this.exceedingMaxlength) {
          this.exceedingMaxlength = false;
          this.notifyExceedingMaxlength(false);
        }
      }
    }
  },
  methods: {
    notifyExceedingMaxlength(val) {
      this.$emit("notifyExceedingMaxlength", val);
    },
    // Special for network-cable-calculator
    keyhandler(e) {
      if (
        this.type === "text" &&
        this.textInputShouldBehaveAsNumber &&
        this.noDecimal
      ) {
        // Isn't e.key a number? Prevent default. But allow backspace, tab and 0
        if (
          !parseInt(e.key) &&
          e.keyCode !== 8 &&
          e.keyCode !== 9 &&
          e.keyCode !== 48 &&
          e.keyCode !== 96
        ) {
          e.preventDefault();
        }
      } else if (this.type === "text" && this.textInputShouldBehaveAsNumber) {
        // Isn't e.key a number? Prevent default. But allow backspace, tab and 0
        if (
          !parseInt(e.key) &&
          e.keyCode !== 8 &&
          e.keyCode !== 188 &&
          e.keyCode !== 9 &&
          e.keyCode !== 48 &&
          e.keyCode !== 96 &&
          e.keyCode !== 110
        ) {
          e.preventDefault();
        }
      }
    },
    getValue() {
      if (this.type === "text") {
        return this.$refs.textvalue.value;
      } else if (this.type === "checkbox") {
        return this.$refs.boolvalue.checked;
      }
      return this.dataValue;
    },
    input(event) {
      this.$emit("input", event.target.value);
    },
    clear() {
      if (this.type === "checkbox") {
        this.checked = null;
        this.$emit("checked", false);
      } else {
        this.value = "";
        this.$emit("input", "");
      }
    },
    checkboxClicked(event) {
      this.$emit("checked", event.target.checked);
    }
  },
  mounted() {
    document
      .querySelector("body")
      .dispatchEvent(
        new Event("preventLeaveSet", this.$refs.invisibleTextarea)
      );
  },
  destroyed() {
    document
      .querySelector("body")
      .dispatchEvent(
        new Event("preventLeaveReset", this.$refs.invisibleTextarea)
      );
  }
};
</script>
